// Pure catalog-cache logic: merging snapshots, applying pending stock, and the
// filters that stand in for the repo's WHERE clauses.
//
// Deliberately free of IndexedDB, fetch and window, because this is the part that
// can be wrong in a way nobody notices — a delta merged badly shows a stale price
// on a screen that looks perfectly healthy. Everything here is unit-tested in
// tests/unit/catalog-cache.test.ts; catalog.ts is the thin stateful shell around it.

import type {
  AppSettings,
  CatalogSnapshot,
  Category,
  Product,
  ProductVariant,
  StoreProfile,
  Location
} from '../../shared/types'

export interface CatalogState {
  /** Server clock from the newest snapshot merged in. Basis for the next cursor. */
  serverTime: string
  /** ALL products, active and inactive. Filtered on read — see filterProducts. */
  products: Product[]
  variants: Record<string, ProductVariant[]>
  categories: Category[]
  settings: AppSettings
  storeProfile: StoreProfile
  locations: Location[]
}

/** One line's worth of stock that a queued sale has taken but the server hasn't seen. */
export interface StockDelta {
  productId: string
  variantId: string | null
  /** Units sold. Positive; subtracted from the cached quantity. */
  quantity: number
}

/**
 * Deterministic order, matching the repo's `ORDER BY name`. The id tiebreak is not
 * decoration: two products can share a name, and an unstable sort would let the
 * grid reshuffle under the cashier's finger on every background revalidate.
 */
function byName(a: Product, b: Product): number {
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
}

/**
 * Fold a snapshot into what we already had.
 *
 * A `full` snapshot replaces everything. A delta upserts products by id and
 * replaces variants per product — per product, not per variant, because the server
 * sends a product's complete variant array whenever any of them moved, which makes
 * "replace the array" the only merge that can also express a variant going away.
 *
 * Categories, settings and the store profile always arrive whole, so they are
 * always replaced. That is what lets a deactivated category actually disappear
 * rather than linger forever as a row nothing updates.
 */
export function mergeSnapshot(prev: CatalogState | null, snap: CatalogSnapshot): CatalogState {
  if (snap.full || !prev) {
    return {
      serverTime: snap.serverTime,
      products: [...snap.products].sort(byName),
      variants: { ...snap.variants },
      categories: snap.categories,
      settings: snap.settings,
      storeProfile: snap.storeProfile,
      locations: snap.locations
    }
  }

  const byId = new Map(prev.products.map((p) => [p.id, p]))
  for (const p of snap.products) byId.set(p.id, p)

  return {
    serverTime: snap.serverTime,
    products: [...byId.values()].sort(byName),
    variants: { ...prev.variants, ...snap.variants },
    categories: snap.categories,
    settings: snap.settings,
    storeProfile: snap.storeProfile,
    locations: snap.locations
  }
}

/**
 * Did this snapshot actually move anything we hold?
 *
 * This decides whether mounted screens are told to refetch, so a false positive is
 * a needless re-render of the whole product grid on every revalidate, and a false
 * negative is a stale price nobody notices.
 *
 * Products and variants answer for themselves: a delta only carries rows that
 * changed, so any row at all means yes. Categories, settings and the profile arrive
 * whole every time and have to be compared — they are small enough (tens of rows,
 * one flat object each) that stringifying them is cheaper than the re-render it
 * avoids, which is emphatically not true of the product list.
 */
export function snapshotChanged(prev: CatalogState | null, snap: CatalogSnapshot): boolean {
  if (!prev || snap.full) return true
  if (snap.products.length > 0 || Object.keys(snap.variants).length > 0) return true
  return (
    JSON.stringify(prev.categories) !== JSON.stringify(snap.categories) ||
    JSON.stringify(prev.settings) !== JSON.stringify(snap.settings) ||
    JSON.stringify(prev.storeProfile) !== JSON.stringify(snap.storeProfile) ||
    JSON.stringify(prev.locations) !== JSON.stringify(snap.locations)
  )
}

/**
 * Subtract sales that are rung up but not yet pushed.
 *
 * Without this, every queued sale visibly un-sells itself: the cache decrements on
 * Charge, then a background revalidate lands with the server's pre-sale quantity
 * (because the sale has not reached it yet) and puts the stock straight back. So the
 * cache holds only what the server said, and pending sales are re-applied on top
 * every time it is read. An entry disappears when its sale is confirmed, at which
 * point the server's number already includes it.
 */
export function applyPendingStock(state: CatalogState, deltas: StockDelta[]): CatalogState {
  if (deltas.length === 0) return state

  const productTaken = new Map<string, number>()
  const variantTaken = new Map<string, number>()
  for (const d of deltas) {
    // The product rollup always moves; the variant cache moves too when sold by variant.
    productTaken.set(d.productId, (productTaken.get(d.productId) ?? 0) + d.quantity)
    if (d.variantId) variantTaken.set(d.variantId, (variantTaken.get(d.variantId) ?? 0) + d.quantity)
  }

  const products = state.products.map((p) => {
    const taken = productTaken.get(p.id)
    return taken ? { ...p, quantity: p.quantity - taken } : p
  })

  const variants: Record<string, ProductVariant[]> = {}
  for (const [productId, list] of Object.entries(state.variants)) {
    variants[productId] = list.map((v) => {
      const taken = variantTaken.get(v.id)
      return taken ? { ...v, quantity: v.quantity - taken } : v
    })
  }

  return { ...state, products, variants }
}

/**
 * The client-side stand-in for listProducts' WHERE clause. It has to agree with
 * src/core/repos/products.repo.ts, or a product findable on a till is unfindable in
 * a browser.
 *
 * Note LOWER() on both sides for name and sku, and an EXACT match on barcode —
 * scanners send it verbatim, and a partial barcode match would ring up the wrong item.
 */
export function filterProducts(
  products: Product[],
  opts: { search?: string; includeInactive?: boolean } = {}
): Product[] {
  const needle = opts.search?.toLowerCase()
  return products.filter((p) => {
    if (!opts.includeInactive && !p.isActive) return false
    if (!needle) return true
    return (
      p.name.toLowerCase().includes(needle) ||
      p.sku.toLowerCase().includes(needle) ||
      p.barcode === opts.search
    )
  })
}

/** Mirrors listCategories: active only unless asked otherwise. */
export function filterCategories(
  categories: Category[],
  opts: { includeInactive?: boolean } = {}
): Category[] {
  return opts.includeInactive ? categories : categories.filter((c) => c.isActive)
}

/**
 * A cursor slightly behind the server's clock.
 *
 * `updated_at` on a row that reached the cloud from a DEVICE carries that device's
 * wall clock, not the server's. A till a minute fast writes a row stamped in the
 * future; a cursor taken at face value would step past it and that edit would stay
 * invisible until the next full refresh. Rewinding costs a few redundant rows per
 * sync and buys a minute of clock skew.
 */
export const CURSOR_SAFETY_MS = 60_000

export function cursorFrom(serverTime: string): string {
  return new Date(Date.parse(serverTime) - CURSOR_SAFETY_MS).toISOString()
}
